/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import { Checkbox } from './Checkbox.js'
import type { DataViewBreakpoint, DataViewColumn, DataViewLayout } from './DataView.js'
import { cellVal, gtCellFlex, gtScopeId, gtSpanStyles } from './DataViewUtils.js'
import { Pagination } from './Pagination.js'
import { Table, TableCaption, TableTbody, TableTd, TableTh, TableThead, TableTr } from './Table.js'
import { cx } from './types.js'

// All props must be JSON-serializable (no functions).
// `columns[].render` is stripped before serialization.
export interface DataViewReactiveProps {
  src: string
  layout: DataViewLayout
  columns: DataViewColumn[]
  initialData: Record<string, unknown>[]
  initialTotal: number
  initialPage: number
  initialSort?: string
  initialOrder: 'asc' | 'desc'
  pageSize: number
  pageParam: string
  sortParam: string
  orderParam: string
  selectable: boolean
  rowKey?: string
  selectName: string
  // table styling (resolved to CSS strings before serialization)
  striped?: boolean | 'odd' | 'even'
  highlightOnHover?: boolean
  withTableBorder?: boolean
  withColumnBorders?: boolean
  withRowBorders: boolean
  horizontalSpacing?: string
  verticalSpacing?: string
  borderColor?: string
  caption?: string
  captionSide?: 'top' | 'bottom'
  tableLayout?: 'auto' | 'fixed'
  // grid
  cols: number
}

// ── Component ─────────────────────────────────────────────────────────────────

function DataViewReactive({
  src,
  layout,
  columns,
  initialData,
  initialTotal,
  initialPage,
  initialSort,
  initialOrder,
  pageSize,
  pageParam,
  sortParam,
  orderParam,
  selectable,
  rowKey,
  selectName,
  striped,
  highlightOnHover,
  withTableBorder,
  withColumnBorders,
  withRowBorders,
  horizontalSpacing,
  verticalSpacing,
  borderColor,
  caption,
  captionSide,
  tableLayout,
  cols,
}: DataViewReactiveProps) {
  const [data, setData] = createSignal<Record<string, unknown>[]>(initialData)
  const [total, setTotal] = createSignal(initialTotal)
  const [page, setPage] = createSignal(initialPage)
  const [sort, setSort] = createSignal<string | undefined>(initialSort)
  const [order, setOrder] = createSignal<'asc' | 'desc'>(initialOrder)
  const [loading, setLoading] = createSignal(false)

  function doFetch(p: number, s: string | undefined, o: 'asc' | 'desc') {
    setLoading(true)
    const params = new URLSearchParams()
    params.set(pageParam, String(p))
    params.set('pageSize', String(pageSize))
    params.set(orderParam, o)
    if (s) params.set(sortParam, s)
    fetch(`${src}?${params}`)
      .then((r) => r.json() as Promise<{ data: Record<string, unknown>[]; total: number }>)
      .then(({ data: rows, total: t }) => {
        setData(rows)
        setTotal(t)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function goToPage(p: number) {
    setPage(p)
    doFetch(p, sort(), order())
  }

  function handleSort(key: string) {
    const newOrder = sort() === key && order() === 'asc' ? 'desc' : 'asc'
    setSort(key)
    setOrder(newOrder)
    setPage(1)
    doFetch(1, key, newOrder)
  }

  // Pre-computed stable values (don't depend on signals)
  const scopeId = gtScopeId(columns)
  const hasCollapseBps = new Set(
    columns.map((c) => c.collapseBelow).filter((bp): bp is DataViewBreakpoint => bp != null),
  )
  const hasCollapseCls = [...hasCollapseBps].map((bp) => `dv-gt-has-collapse-${bp}`).join(' ')
  const spanCss = gtSpanStyles(columns, scopeId)
  const titleCol = columns.find((c) => c.role === 'title') ?? columns[0]
  const subtitleCol = columns.find((c) => c.role === 'subtitle')
  const detailCols = columns.filter((c) => c !== titleCol && c !== subtitleCol)
  const collapseCols = columns.filter((c) => c.collapseBelow)

  const tableCssStr =
    [
      horizontalSpacing ? `--dv-table-hz-spacing:${horizontalSpacing}` : '',
      verticalSpacing ? `--dv-table-v-spacing:${verticalSpacing}` : '',
      borderColor ? `--dv-table-border-color:${borderColor}` : '',
      captionSide ? `--dv-table-caption-side:${captionSide}` : '',
    ]
      .filter(Boolean)
      .join(';') || undefined

  // ── Inner renderers — called inside the single reactive {() => ...} ─────────

  function renderSortTh(
    col: DataViewColumn,
    currentSort: string | undefined,
    currentOrder: 'asc' | 'desc',
  ) {
    const isSorted = currentSort === col.key
    const icon = isSorted ? (currentOrder === 'asc' ? '↑' : '↓') : '↕'
    return (
      <TableTh
        align={col.align}
        width={col.width}
        aria-sort={isSorted ? (currentOrder === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        {col.sortable ? (
          <button
            type='button'
            class='dv-data-view-sort-link'
            data-sorted={isSorted || undefined}
            onClick={() => handleSort(col.key)}
          >
            {col.label}
            <span class='dv-data-view-sort-icon' aria-hidden='true'>
              {icon}
            </span>
          </button>
        ) : (
          col.label
        )}
      </TableTh>
    )
  }

  function renderTable(
    rows: Record<string, unknown>[],
    currentSort: string | undefined,
    currentOrder: 'asc' | 'desc',
  ) {
    const colCount = columns.length + (selectable ? 1 : 0)
    return (
      <Table
        style={tableCssStr}
        striped={striped}
        highlightOnHover={highlightOnHover}
        withTableBorder={withTableBorder}
        withColumnBorders={withColumnBorders}
        withRowBorders={withRowBorders}
        data-layout={tableLayout}
      >
        {caption && <TableCaption>{caption}</TableCaption>}
        <TableThead>
          <TableTr>
            {selectable && (
              <TableTh class='dv-data-view-select-col'>
                <Checkbox data-dv-select-all='' aria-label='Select all rows' />
              </TableTh>
            )}
            {columns.map((col) => renderSortTh(col, currentSort, currentOrder))}
          </TableTr>
        </TableThead>
        <TableTbody>
          {rows.length === 0 ? (
            <TableTr>
              <TableTd class='dv-data-view-empty' colspan={colCount}>
                No results found
              </TableTd>
            </TableTr>
          ) : (
            rows.map((row, i) => (
              <TableTr>
                {selectable && (
                  <TableTd class='dv-data-view-select-col'>
                    <Checkbox
                      name={selectName}
                      value={rowKey ? String(row[rowKey] ?? i) : String(i)}
                      data-dv-select-row=''
                      aria-label='Select row'
                    />
                  </TableTd>
                )}
                {columns.map((col) => (
                  <TableTd align={col.align}>{cellVal(row, col)}</TableTd>
                ))}
              </TableTr>
            ))
          )}
        </TableTbody>
      </Table>
    )
  }

  function renderCards(rows: Record<string, unknown>[]) {
    if (rows.length === 0) return <div class='dv-data-view-empty'>No results found</div>
    return (
      <div class='dv-data-view-cards-wrapper'>
        {selectable && (
          <div class='dv-data-view-select-toolbar'>
            <Checkbox data-dv-select-all='' aria-label='Select all rows' label='Select all' />
          </div>
        )}
        <ul class='dv-data-view-cards'>
          {rows.map((row, i) => {
            const value = rowKey ? String(row[rowKey] ?? i) : String(i)
            return (
              <li class='dv-data-view-card'>
                {selectable && (
                  <Checkbox
                    name={selectName}
                    value={value}
                    data-dv-select-row=''
                    aria-label={titleCol ? `Select ${cellVal(row, titleCol)}` : 'Select row'}
                  />
                )}
                {titleCol && <div class='dv-data-view-card-title'>{cellVal(row, titleCol)}</div>}
                {subtitleCol && (
                  <div class='dv-data-view-card-subtitle'>{cellVal(row, subtitleCol)}</div>
                )}
                {detailCols.length > 0 && (
                  <dl class='dv-data-view-card-fields'>
                    {detailCols.map((col) => (
                      <div class='dv-data-view-field'>
                        <dt class='dv-data-view-field-label'>{col.label}</dt>
                        <dd class='dv-data-view-field-value'>{cellVal(row, col)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  function renderGrid(rows: Record<string, unknown>[]) {
    if (rows.length === 0) return <div class='dv-data-view-empty'>No results found</div>
    return (
      <div class='dv-data-view-grid-wrapper'>
        {selectable && (
          <div class='dv-data-view-select-toolbar'>
            <Checkbox data-dv-select-all='' aria-label='Select all rows' label='Slect all' />
            <span class='dv-data-view-select-label'>Select all</span>
          </div>
        )}
        <ul class='dv-data-view-grid' style={`--dv-data-view-cols:${cols}`}>
          {rows.map((row, i) => {
            const value = rowKey ? String(row[rowKey] ?? i) : String(i)
            return (
              <li class='dv-data-view-grid-item'>
                <div class='dv-data-view-grid-item-header'>
                  {selectable && (
                    <Checkbox
                      name={selectName}
                      value={value}
                      data-dv-select-row=''
                      aria-label={titleCol ? `Select ${cellVal(row, titleCol)}` : 'Select row'}
                    />
                  )}
                  {titleCol && (
                    <div class='dv-data-view-grid-item-title'>{cellVal(row, titleCol)}</div>
                  )}
                </div>
                {detailCols.length > 0 && (
                  <dl class='dv-data-view-grid-item-fields'>
                    {detailCols.map((col) => (
                      <div class='dv-data-view-field'>
                        <dt class='dv-data-view-field-label'>{col.label}</dt>
                        <dd class='dv-data-view-field-value'>{cellVal(row, col)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  function renderGtRow(
    row: Record<string, unknown>,
    i: number,
    currentSort: string | undefined,
    currentOrder: 'asc' | 'desc',
  ) {
    const value = rowKey ? String(row[rowKey] ?? i) : String(i)
    const checkbox = selectable && (
      <Checkbox
        class='dv-data-view-card-select'
        name={selectName}
        value={value}
        data-dv-select-row=''
        aria-label='Select row'
      />
    )
    const rowCells = columns.map((col) => {
      const cellCls = cx(
        'dv-gt-cell',
        col.hideBelow ? `dv-gt-hide-${col.hideBelow}` : undefined,
        col.collapseBelow ? `dv-gt-collapse-${col.collapseBelow}` : undefined,
        typeof col.span === 'object' ? `dv-gt-cell-${scopeId}-${col.key}` : undefined,
      )
      const cellStyle =
        [gtCellFlex(col), col.align ? `text-align:${col.align}` : ''].filter(Boolean).join(';') ||
        undefined
      return (
        <div class={cellCls} style={cellStyle}>
          {cellVal(row, col)}
        </div>
      )
    })
    const expandedFields = collapseCols.map((col) => (
      <div class={`dv-gt-field dv-gt-collapse-expanded-${col.collapseBelow}`}>
        <span class='dv-gt-field-label'>{col.label}</span>
        <span class='dv-gt-field-value'>{cellVal(row, col)}</span>
      </div>
    ))

    if (collapseCols.length === 0) {
      return (
        <div class='dv-gt-row'>
          {selectable && <div class='dv-gt-cell dv-gt-select-col'>{checkbox}</div>}
          {rowCells}
        </div>
      )
    }
    return (
      <details class={cx('dv-gt-details', hasCollapseCls)}>
        <summary class='dv-gt-row'>
          <div class='dv-gt-cell dv-gt-expand-col' aria-hidden='true'>
            <svg
              class='dv-gt-chevron'
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
              aria-hidden='true'
            >
              <path
                d='M2 4L6 8L10 4'
                stroke='currentColor'
                stroke-width='1.75'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>
          </div>
          {selectable && <div class='dv-gt-cell dv-gt-select-col'>{checkbox}</div>}
          {rowCells}
        </summary>
        <div class='dv-gt-expanded'>{expandedFields}</div>
      </details>
    )
  }

  function renderGridTable(
    rows: Record<string, unknown>[],
    currentSort: string | undefined,
    currentOrder: 'asc' | 'desc',
  ) {
    return (
      <div class={cx('dv-gt-wrapper', `dv-gt-scope-${scopeId}`, hasCollapseCls)}>
        {spanCss && <style>{spanCss}</style>}
        <div class='dv-gt-row dv-gt-header-row'>
          <div class='dv-gt-cell dv-gt-expand-col' aria-hidden='true' />
          {selectable && <div class='dv-gt-cell dv-gt-select-col' />}
          {columns.map((col) => {
            const isSorted = currentSort === col.key
            const icon = isSorted ? (currentOrder === 'asc' ? '↑' : '↓') : '↕'
            const thCls = cx(
              'dv-gt-cell',
              'dv-gt-th',
              col.hideBelow ? `dv-gt-hide-${col.hideBelow}` : undefined,
              col.collapseBelow ? `dv-gt-collapse-${col.collapseBelow}` : undefined,
              typeof col.span === 'object' ? `dv-gt-cell-${scopeId}-${col.key}` : undefined,
            )
            const thStyle =
              [gtCellFlex(col), col.align ? `text-align:${col.align}` : '']
                .filter(Boolean)
                .join(';') || undefined
            return (
              <div class={thCls} style={thStyle}>
                {col.sortable ? (
                  <button
                    type='button'
                    class='dv-data-view-sort-link'
                    data-sorted={isSorted || undefined}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <span class='dv-data-view-sort-icon' aria-hidden='true'>
                      {icon}
                    </span>
                  </button>
                ) : (
                  col.label
                )}
              </div>
            )
          })}
        </div>
        {rows.length === 0 ? (
          <div class='dv-data-view-empty'>No results found</div>
        ) : (
          <div class='dv-gt-body'>
            {rows.map((row, i) => renderGtRow(row, i, currentSort, currentOrder))}
          </div>
        )}
      </div>
    )
  }

  function renderPagination(currentPage: number, currentTotal: number) {
    const totalPages = Math.max(1, Math.ceil(currentTotal / pageSize))
    if (totalPages <= 1) return null
    const start = currentTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1
    const end = Math.min(currentPage * pageSize, currentTotal)
    return (
      <div class='dv-data-view-pagination'>
        <span class='dv-data-view-pagination-info'>
          {start}–{end} of {currentTotal}
        </span>
        <Pagination total={totalPages} value={currentPage} onClick={goToPage} withControls />
      </div>
    )
  }

  // Single reactive computation — re-runs when any signal changes
  return (
    <div class='dv-data-view'>
      {() => {
        const rows = data()
        const p = page()
        const s = sort()
        const o = order()
        const t = total()
        const isLoading = loading()

        const content =
          layout === 'table'
            ? renderTable(rows, s, o)
            : layout === 'grid-table'
              ? renderGridTable(rows, s, o)
              : layout === 'list' || layout === 'cards'
                ? renderCards(rows)
                : layout === 'grid'
                  ? renderGrid(rows)
                  : renderTable(rows, s, o)

        return (
          <div
            data-loading={isLoading || undefined}
            style={
              isLoading
                ? 'opacity:0.55;pointer-events:none;transition:opacity 150ms ease'
                : undefined
            }
          >
            {content}
            {renderPagination(p, t)}
          </div>
        )
      }}
    </div>
  )
}

export default reactive(DataViewReactive)
